import type { Database } from "@/integrations/supabase/types";

type ProfileUpdate = Database["public"]["Tables"]["profiles"]["Update"];

/**
 * Columns an authenticated browser session may write on its own profile.
 *
 * This list must equal the `authenticated` column-level UPDATE grants on
 * public.profiles exactly. That equality is not left to this comment: the
 * disposable-database harness (scripts/od1-license-field-local-check.mjs)
 * derives the grant set from pg_attribute.attacl and asserts set equality with
 * this array, so adding a field here that the database does not grant fails
 * the harness rather than producing a 403 in front of a user.
 *
 * Source-derived license/DRE fields are intentionally absent; license_number is
 * present because it is the user's assertion, not evidence.
 *
 * NOTE ON TYPING. `keyof ProfileUpdate` proves each name is a real profiles
 * column and NOTHING MORE — it would happily accept affiliated_org_id, is_seed
 * or slug, none of which the browser may write. The type stops typos; the
 * harness stops the real mistake.
 */
export const OWN_PROFILE_UPDATE_FIELDS = [
  "full_name",
  "email",
  "avatar_url",
  "header_image_url",
  "profile_data",
  "privacy_visibility_tier",
  "privacy_allow_contact",
  "privacy_show_metrics",
  "privacy_show_photo",
  "privacy_show_name",
  "privacy_show_brokerage",
  "pref_compensation_models",
  "pref_wants_leads",
  "pref_no_recurring_fees",
  "pref_wants_partnership",
  "pref_service_types",
  "pref_required_benefits",
  "pref_training_frequency",
  "pref_importance_leadership",
  "pref_importance_training",
  "pref_importance_commission",
  "pref_importance_work_life",
  "pref_importance_technology",
  "license_number",
] as const satisfies readonly (keyof ProfileUpdate)[];

export type OwnProfileUpdateField = (typeof OWN_PROFILE_UPDATE_FIELDS)[number];
export type OwnProfileUpdate = Partial<Pick<ProfileUpdate, OwnProfileUpdateField>>;

const ownProfileUpdateFieldSet = new Set<string>(OWN_PROFILE_UPDATE_FIELDS);

/**
 * Raised when a caller asks to write a profiles column the browser may not
 * write. It carries the offending field names so the failure is diagnosable
 * without guessing.
 */
export class UnsupportedOwnProfileFieldError extends Error {
  readonly fields: readonly string[];

  constructor(fields: readonly string[]) {
    super(
      `Refusing to save profile fields the browser may not write: ${fields.join(", ")}. ` +
        "Source-derived license and DRE fields are written server-side only; " +
        "everything else must be added to OWN_PROFILE_UPDATE_FIELDS and granted in a migration.",
    );
    this.name = "UnsupportedOwnProfileFieldError";
    this.fields = fields;
  }
}

export interface OwnProfileUpdateSplit {
  /** The fields that may be sent to PostgREST. */
  patch: OwnProfileUpdate;
  /**
   * Defined fields that were removed. An `undefined` value is NOT reported:
   * omitting it is ordinary patch semantics and carries no intent to write.
   */
  dropped: string[];
}

/**
 * Split a proposed patch into what may be written and what may not.
 *
 * Use this when the caller wants to inspect the outcome. Use
 * sanitizeOwnProfileUpdate when the caller wants the write to fail loudly,
 * which is almost always the right answer.
 */
export function partitionOwnProfileUpdate(
  patch: ProfileUpdate | Record<string, unknown>,
): OwnProfileUpdateSplit {
  const safePatch: Record<string, unknown> = {};
  const dropped: string[] = [];

  for (const [field, value] of Object.entries(patch)) {
    if (value === undefined) continue;
    if (ownProfileUpdateFieldSet.has(field)) {
      safePatch[field] = value;
    } else {
      dropped.push(field);
    }
  }

  return { patch: safePatch as OwnProfileUpdate, dropped };
}

/**
 * Fail closed at the browser boundary.
 *
 * Unknown, system-managed and source-derived fields never reach PostgREST.
 *
 * WHY THIS THROWS RATHER THAN DROPPING QUIETLY. An earlier form of this
 * function removed unsupported fields and returned. That trades a visible 403
 * for an invisible one: the request succeeds, the UI says "Saved", and the
 * field the caller meant to persist is simply gone. Silent data loss is worse
 * than a failed save, and it is undetectable in Sentry. Every call site already
 * reports a save failure to the user, so throwing surfaces the mistake in
 * development and degrades to an ordinary error toast in production.
 */
export function sanitizeOwnProfileUpdate(
  patch: ProfileUpdate | Record<string, unknown>,
): OwnProfileUpdate {
  const { patch: safePatch, dropped } = partitionOwnProfileUpdate(patch);
  if (dropped.length > 0) {
    throw new UnsupportedOwnProfileFieldError(dropped);
  }
  return safePatch;
}
